import {
  Component,
  OnInit,
  Input,
  ChangeDetectionStrategy,
  OnDestroy,
  HostListener,
  ElementRef,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Location } from '@angular/common';
import {
  User,
  MenuApiService,
  AuthService,
  FavoriteService,
  MenuItems,
  Category,
  Application,
  FeatureFlagService,
} from '@icullen/api/shared';
import { NavigationUtilsService } from './navigation-utils.service';
import { filter, interval, Subscription } from 'rxjs';
import { TopicsService } from '@icullen/api/site';
import { Notifications } from 'libs/api/site/src/lib/model/notifications.model';

@Component({
  selector: 'main-navigation',
  templateUrl: './main-navigation.component.html',
  //changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./main-navigation.component.scss'],
})
export class MainNavigationComponent implements OnInit, OnDestroy {
  menuItems: MenuItems;
  currentUser: User;
  mobileMenuIsOpen = false;
  mobileNotificationsIsOpen = false;
  mobileProfileIsOpen = false;
  mobileSearchIsShow = false;
  activeSublist: string;
  //favoritesCount: number;
  private subs = new Subscription();

  notifications: Notifications | null = null;
  //load every 15 minutes
  private notificationInterval = 60 * 10 * 1000;
  showNotifications = false;
  private isPollingStarted = false;

  @Input() isGenesisContext = false;

  activeLink = 'my-cullen';
  favDocs$ = this.favoriteService.observableFavDocs;

  constructor(
    private router: Router,
    private location: Location,
    private menuService: MenuApiService,
    private authService: AuthService,
    private favoriteService: FavoriteService,
    private navigationUtils: NavigationUtilsService,
    public featureFlagService: FeatureFlagService,
    private topicsService: TopicsService,
    private elementRef: ElementRef
  ) {
    //  authService.currentUserSubject.subscribe((user) => {
    //     this.currentUser=user;
    //  });

    if (this.authService.isAuthenticated()) {
      this.loadMenu();
    }

    this.authService.authenticationEventObservable.subscribe(result => {
      if (result) {
        this.loadMenu();
      }
    });
  }

  private loadMenu() {
    this.currentUser = this.authService.getCurrentUser();
    this.favoriteService.loadFavoriteDocs();

    // Charger les notifications
    this.loadNotifications();
    this.startNotificationPolling();

    this.subs.add(
      this.menuService.getMenuItems(this.currentUser?.username).subscribe((data: MenuItems) => {
        this.menuItems = data;
        // Update categories in NavigationUtilsService
        if (this.menuItems && this.menuItems.categories) {
          this.navigationUtils.setCategories(this.menuItems.categories);
        }
        this.filterApps();
      })
    );
    this.setActiveLink();
  }

  /**
   * Charge les notifications pour l'utilisateur actuel
   */
  private loadNotifications(): void {
    if (this.currentUser?.preferred_username) {
      this.subs.add(
        this.topicsService.getNotifications().subscribe({
          next: (notifications: Notifications) => {
            this.notifications = notifications;
            console.log('Notifications loaded:', notifications);
          },
          error: error => {
            console.error('Error loading notifications:', error);
          },
        })
      );
    }
  }

  /**
   * polling every minute
   */
  private startNotificationPolling(): void {
    if (this.isPollingStarted) return;
    this.isPollingStarted = true;
    this.subs.add(
      interval(this.notificationInterval).subscribe(() => {
        this.loadNotifications();
      })
    );
  }

  /**
   * Getter pour obtenir le nombre de notifications non vues
   */
  get unseenNotificationsCount(): number {
    return this.notifications?.unseenCount || 0;
  }

  /**
   * Action on click : ouvrir les notifs et les marquées comme vues
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const notificationContainer = this.elementRef.nativeElement.querySelector('.Icon--notifications');
    if (this.showNotifications && notificationContainer && !notificationContainer.contains(target)) {
      this.showNotifications = false;
    }
  }
  seeNotificationsOnClick() {
    this.showNotifications = !this.showNotifications;
    if (this.showNotifications && this.currentUser?.preferred_username) {
      this.subs.add(
        this.topicsService.markNotificationsAsViewed().subscribe({
          next: () => {
            if (this.notifications) {
              this.notifications.unseenCount = 0;
            }
            console.log('Notifications marked as viewed');
          },
          error: error => {
            console.error('Error marking notifications as viewed:', error);
          },
        })
      );
    }
  }

  closeNotificationsOnClick() {
    this.showNotifications = false;
  }

  isNonCullenUser(): boolean {
    return this.currentUser?.UserType !== 'CULLEN_USER';
  }

  disableScrolling() {
    const x = window.scrollX;
    const y = window.scrollY;
    window.onscroll = function () {
      window.scrollTo(x, y);
    };
  }

  enableScrolling() {
    window.onscroll = function () {};
  }

  showSublistOnClick(clickedIcon: string) {
    this.activeSublist = clickedIcon;
  }

  openMobileMenuOnClick() {
    this.mobileMenuIsOpen = true;
    this.disableScrolling();
  }

  closeMobileMenuOnClick() {
    this.mobileMenuIsOpen = false;
    this.enableScrolling();
  }

  openMobileProfileOnClick() {
    this.mobileProfileIsOpen = true;
    this.disableScrolling();
  }

  closeMobileProfileOnClick() {
    this.mobileProfileIsOpen = false;
    this.enableScrolling();
  }

  openMobileNotificationsOnClick() {
    this.mobileNotificationsIsOpen = true;
    this.disableScrolling();
    if (this.mobileNotificationsIsOpen && this.currentUser?.preferred_username) {
      this.subs.add(
        this.topicsService.markNotificationsAsViewed().subscribe({
          next: () => {
            if (this.notifications) {
              this.notifications.unseenCount = 0;
            }
            console.log('Notifications marked as viewed');
          },
          error: error => {
            console.error('Error marking notifications as viewed:', error);
          },
        })
      );
    }
  }

  closeMobileNotificationsOnClick() {
    this.mobileNotificationsIsOpen = false;
    this.enableScrolling();
  }

  showMobileSearchOnClick() {
    this.mobileSearchIsShow = true;
  }

  hideMobileSearchOnClick() {
    this.mobileSearchIsShow = false;
  }

  ngOnInit() {
    // this.favoriteService.observableFavDocs
    //   .subscribe(favDocs => this.favoritesCount=favDocs.length);
    this.subs.add(
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        if (this.showNotifications) {
          this.closeNotificationsOnClick();
        }
      })
  );
  }

  login() {
    if (this.currentUser && this.currentUser?.partialAuth) {
      let redirectUrl = this.router.url.split('?')[0];
      this.authService.logoutWithRedirectUrl(redirectUrl);
    } else {
      this.authService.loginSso('');
    }
  }

  setActiveLink() {
    const currentPath = this.location.path();
    if (currentPath.startsWith('/global')) {
      this.activeLink = 'global';
    } else if (currentPath.startsWith('/product/documents') || currentPath.startsWith('/product/headlines')) {
      this.activeLink = 'my-cullen';
    } else if (currentPath.indexOf('applications') > -1) {
      this.activeLink = 'applications';
    } else if (currentPath.startsWith('/site/toolbox')) {
      this.activeLink = 'toolbox';
    } else if (currentPath.startsWith('/trainings')) {
      this.activeLink = 'trainings';
    } else {
      this.activeLink = 'my-cullen';
    }
  }

  filterApps() {
    const apps: Application[] = [];

    if (this.menuItems && this.menuItems.applications) {
      this.menuItems.applications.forEach((app: Application) => {
        if (app) {
          apps.push(this.navigationUtils.transformApp(app));
        }
      });
    }

    if (this.menuItems && this.menuItems.categories) {
      this.menuItems.categories.forEach((category: Category) => {
        if (category.applications) {
          category.applications.forEach((app: Application) => {
            apps.push({
              name: app.name,
              uniquenumber: app.uniquenumber,
              docApp: true,
            });
          });
        }
      });
    }

    this.menuItems.applications = apps.filter((app, index) => {
      return (
        index ===
        apps.findIndex(obj => {
          return JSON.stringify(obj) === JSON.stringify(app);
        })
      );
    });
  }

  getAppNavUrl(app: Application) {
    return this.navigationUtils.getAppNavUrl(app);
  }

  logout() {
    this.authService.logout();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
